/**
 * QuickAddHeader — the title bar of the QuickAdd modal.
 *
 * Shows the active mode (Quick add / Brain dump), the keyboard
 * shortcut hint, an optional brain-dump session counter, the
 * brain-dump toggle, and the close button.
 *
 * Pulled out of QuickAddModal.js so the modal's main body reads
 * as: header → body → chips → footer, each ~30 lines.
 */
import { X, Plus, Brain, Inbox as InboxIcon, Tags } from 'lucide-react';

export function QuickAddHeader({
  brainDump,
  capturedThisSession,
  inboxCount,
  routeToInbox,
  onToggleBrainDump,
  onToggleRouteToInbox,
  onClose,
}) {
  return (
    <div
      className="px-5 py-3 flex items-center justify-between"
      style={{ borderBottom: '1px solid var(--pd-border)' }}
    >
      <div className="flex items-center gap-2">
        {brainDump ? (
          <Brain className="w-4 h-4" style={{ color: 'var(--pd-brand)' }} />
        ) : (
          <Plus className="w-4 h-4" style={{ color: 'var(--pd-accent)' }} />
        )}
        <div
          className="text-[12px] font-semibold"
          style={{ color: 'var(--pd-text)' }}
          data-testid="quickadd-mode-label"
        >
          {brainDump ? 'Brain dump' : 'Quick add'}
        </div>
        <span
          className="text-[10px] px-1.5 py-0.5 rounded"
          style={{ background: 'var(--pd-panel-2)', color: 'var(--pd-subtle)' }}
        >
          {brainDump ? '⇧⌘D' : '⌘N'}
        </span>

        {/* Brain-dump session counter — "X captured · Y in inbox". */}
        {brainDump ? (
          <span
            data-testid="quickadd-braindump-counter"
            className="text-[10px] ml-1.5"
            style={{ color: 'var(--pd-subtle)' }}
          >
            <span
              data-testid="quickadd-captured-count"
              style={{ color: 'var(--pd-brand)', fontWeight: 600 }}
            >
              {capturedThisSession}
            </span>
            {' '}captured
            {typeof inboxCount === 'number' ? (
              <>
                {' · '}
                <span data-testid="quickadd-inbox-count">{inboxCount}</span>
                {' in inbox'}
              </>
            ) : null}
          </span>
        ) : null}
      </div>
      <div className="flex items-center gap-1">
        {/* Routing toggle — flips capture target between the typed
            collection and the Inbox triage queue. Off-state shows
            the chosen type label so the user sees WHERE the save
            will land at a glance ("→ Task" vs "→ Inbox"). The pill
            mirrors brain-dump's toggle styling so the modal reads
            as having one consistent control language. */}
        <button
          type="button"
          data-testid="quickadd-route-toggle"
          onClick={onToggleRouteToInbox}
          className="px-2 py-1 rounded-md text-[10px] font-medium flex items-center gap-1 transition-colors"
          style={{
            background: routeToInbox ? 'var(--pd-focus-soft, var(--pd-panel-2))' : 'var(--pd-panel-2)',
            color: routeToInbox ? 'var(--pd-focus)' : 'var(--pd-subtle)',
            border: '1px solid ' + (routeToInbox ? 'var(--pd-focus)' : 'var(--pd-border)'),
          }}
          title={
            routeToInbox
              ? 'Currently routing to Inbox (triage later). Click to send directly to the typed collection.'
              : 'Currently routing to the typed collection. Click to send to Inbox for later triage.'
          }
          aria-pressed={routeToInbox}
        >
          {routeToInbox ? <InboxIcon className="w-3 h-3" /> : <Tags className="w-3 h-3" />}
          {routeToInbox ? 'Inbox' : 'Type'}
        </button>
        <button
          type="button"
          data-testid="quickadd-braindump-toggle"
          onClick={onToggleBrainDump}
          className="px-2 py-1 rounded-md text-[10px] font-medium flex items-center gap-1 transition-colors"
          style={{
            // Brain Dump is the warm, rapid-capture flow — uses the
            // brand amber yellow when active (instead of the
            // monochrome `--pd-accent` which renders too cold/grey
            // for a creative-flow affordance). Inactive state stays
            // muted so the toggle reads as a "tap to switch on"
            // affordance, not a competing CTA.
            background: brainDump ? 'var(--pd-brand)' : 'var(--pd-panel-2)',
            color: brainDump ? '#1A1300' : 'var(--pd-subtle)',
            border: '1px solid ' + (brainDump ? 'var(--pd-brand)' : 'var(--pd-border)'),
          }}
          title={brainDump ? 'Exit brain dump' : 'Enter brain dump mode (Shift+Cmd+D)'}
          aria-pressed={brainDump}
        >
          <Brain className="w-3 h-3" />
          {brainDump ? 'On' : 'Brain dump'}
        </button>
        <button data-testid="quickadd-close" onClick={onClose} className="pd-icon-button">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
